import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import {
  flattenRecommendations,
  parseInspectionRecommendations,
} from 'features/libra/stepComponents/niiReport/inspectionRecommendations'
import { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { LibraWorkflowGlobalContext } from 'features/libra/workflow/workflowStepsApi'
import { z } from 'zod'

import {
  PlanningPreparationConfig,
  planningPreparationSchema,
} from './planningPreparationConfig'
import { getPlanningStrategy } from './planningStrategy'
import {
  NiiAssessmentReference,
  niiAssessmentReferenceSchema,
  PlanningSourceCapture,
  planningSourceCaptureSchema,
  planningStrategiesSchema,
  sourceZonesSchema,
} from './planningTypes'
import {
  buildSourceZoneSections,
  sourceContextSchema,
  SourceContextSection,
} from './sourceContext'

const capturedBasisSchema = z.object({
  source: niiAssessmentReferenceSchema,
  zones: sourceZonesSchema,
  context: sourceContextSchema.nullish(),
  summary: z.string().nullish(),
  strategies: planningStrategiesSchema,
})

export type PlanningBasis =
  | { status: 'invalid'; error: z.ZodError }
  | { status: 'uncaptured'; captureConfig: PlanningSourceCapture }
  | {
      status: 'ready'
      config: PlanningPreparationConfig
      source: NiiAssessmentReference
      zones: ZoneParams[]
      coverageByZone: Record<string, number | undefined>
      capturedSections: SourceContextSection[] | undefined
      sourceDetails: { key: string; label: string; value: string }[]
      summary: string | null | undefined
      recommendations: string[]
    }

export function readPlanningBasis(
  rawConfig: Record<string, unknown>,
  fieldValues: FieldValues,
  globalContext: LibraWorkflowGlobalContext
): PlanningBasis {
  const preparation = planningPreparationSchema.safeParse(rawConfig)
  if (!preparation.success)
    return { status: 'invalid', error: preparation.error }
  const config = preparation.data
  const source = unwrapFieldValue(fieldValues[config.sourceField])
  if (source === undefined || source === null) {
    const capture = planningSourceCaptureSchema.safeParse(
      globalContext.planningSourceCapture
    )
    return capture.success
      ? { status: 'uncaptured', captureConfig: capture.data }
      : { status: 'invalid', error: capture.error }
  }
  const rawZones = unwrapFieldValue(fieldValues[config.sourceZonesField])
  const parsed = capturedBasisSchema.safeParse({
    source,
    zones: rawZones,
    context: config.sourceContext
      ? unwrapFieldValue(fieldValues[config.sourceContext.field])
      : undefined,
    summary: unwrapFieldValue(fieldValues[config.summaryField]),
    strategies: globalContext.inspectionPlanningStrategies,
  })
  if (!parsed.success) return { status: 'invalid', error: parsed.error }
  const { zones, context, summary, strategies } = parsed.data
  return {
    status: 'ready',
    config,
    source: parsed.data.source,
    zones,
    summary,
    coverageByZone: Object.fromEntries(
      zones.map((zone) => [
        zone.id,
        getPlanningStrategy(zone, strategies).plannedCoveragePercent,
      ])
    ),
    capturedSections: context
      ? [
          ...context.sections,
          ...buildSourceZoneSections(
            rawZones,
            config.sourceContext?.zoneFields
          ),
        ]
      : undefined,
    sourceDetails: config.sourceDetails.map(({ field, label }) => {
      const value = unwrapFieldValue(fieldValues[field])
      return {
        key: field,
        label,
        value:
          typeof value === 'string' && value.trim()
            ? value
            : 'Not captured in this plan',
      }
    }),
    recommendations: flattenRecommendations(
      parseInspectionRecommendations({
        inspection_recommendations: fieldValues[config.recommendationsField],
      }),
      zones
    ),
  }
}
